import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVELS = [
    { value: "apporteur", label: "Apporteur" },
    { value: "business_partner", label: "Business Partner" },
    { value: "master_partner", label: "Master Partner" },
];

const NETWORK_TYPES = [
    { value: "travel", label: "Travel" },
    { value: "gaming", label: "Gaming" },
    { value: "diaspora", label: "Communautaire / Diaspora" },
    { value: "marketplace", label: "Marketplace / E-commerce" },
    { value: "b2b", label: "Réseaux B2B généraux" },
    { value: "autre", label: "Autre" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EnrollmentForm({ open, onOpenChange, preselectedLevel }) {
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [form, setForm] = useState(initialForm(preselectedLevel));
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (open) {
            setForm((f) => ({ ...f, level: preselectedLevel || f.level }));
            setSubmitted(false);
            setErrors({});
        }
    }, [open, preselectedLevel]);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const validate = () => {
        const e = {};
        if (!form.first_name.trim()) e.first_name = "Champ requis";
        if (!form.last_name.trim()) e.last_name = "Champ requis";
        if (!form.email.trim()) e.email = "Champ requis";
        else if (!EMAIL_RE.test(form.email)) e.email = "Email invalide";
        if (!form.phone.trim()) e.phone = "Champ requis";
        if (!form.country_region.trim()) e.country_region = "Champ requis";
        if (!form.level) e.level = "Champ requis";
        if (!form.network_type) e.network_type = "Champ requis";
        if (!form.network_description.trim() || form.network_description.trim().length < 20)
            e.network_description = "Décrivez votre réseau (20 caractères min)";
        if (!form.consent) e.consent = "Consentement requis";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            toast.error("Merci de corriger les champs en rouge.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/applications`, form);
            setSubmitted(true);
            toast.success("Candidature envoyée ✓");
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                "Une erreur est survenue. Réessayez dans un instant.";
            toast.error(typeof msg === "string" ? msg : "Erreur");
        } finally {
            setSubmitting(false);
        }
    };

    const close = () => {
        onOpenChange(false);
        setTimeout(() => {
            setForm(initialForm(preselectedLevel));
            setSubmitted(false);
            setErrors({});
        }, 200);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl max-h-[92vh] overflow-y-auto"
                data-testid="enrollment-dialog"
            >
                {submitted ? (
                    <SuccessView onClose={close} />
                ) : (
                    <form onSubmit={submit} className="space-y-5">
                        <DialogHeader>
                            <DialogTitle
                                className="h-display text-2xl text-cap-ink"
                                data-testid="enrollment-title"
                            >
                                Rejoindre CapDistrib
                            </DialogTitle>
                            <DialogDescription className="text-cap-textMuted">
                                Réponse sous 48h. Aucun engagement initial.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field
                                label="Prénom *"
                                error={errors.first_name}
                                testid="field-firstname"
                            >
                                <Input
                                    value={form.first_name}
                                    onChange={(e) => setField("first_name", e.target.value)}
                                    placeholder="Camille"
                                    data-testid="input-firstname"
                                />
                            </Field>
                            <Field label="Nom *" error={errors.last_name} testid="field-lastname">
                                <Input
                                    value={form.last_name}
                                    onChange={(e) => setField("last_name", e.target.value)}
                                    placeholder="Durand"
                                    data-testid="input-lastname"
                                />
                            </Field>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Email pro *" error={errors.email} testid="field-email">
                                <Input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setField("email", e.target.value)}
                                    placeholder="vous@entreprise.com"
                                    data-testid="input-email"
                                />
                            </Field>
                            <Field label="Téléphone *" error={errors.phone} testid="field-phone">
                                <Input
                                    value={form.phone}
                                    onChange={(e) => setField("phone", e.target.value)}
                                    placeholder="+33 6 12 34 56 78"
                                    data-testid="input-phone"
                                />
                            </Field>
                        </div>

                        <Field
                            label="Pays / Région d'activité *"
                            error={errors.country_region}
                            testid="field-region"
                        >
                            <Input
                                value={form.country_region}
                                onChange={(e) => setField("country_region", e.target.value)}
                                placeholder="ex. France — Île-de-France"
                                data-testid="input-region"
                            />
                        </Field>

                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Niveau souhaité *" error={errors.level} testid="field-level">
                                <Select
                                    value={form.level}
                                    onValueChange={(v) => setField("level", v)}
                                >
                                    <SelectTrigger data-testid="select-level">
                                        <SelectValue placeholder="Choisir un niveau" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {LEVELS.map((l) => (
                                            <SelectItem
                                                key={l.value}
                                                value={l.value}
                                                data-testid={`level-option-${l.value}`}
                                            >
                                                {l.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field
                                label="Type de réseau / vertical *"
                                error={errors.network_type}
                                testid="field-network"
                            >
                                <Select
                                    value={form.network_type}
                                    onValueChange={(v) => setField("network_type", v)}
                                >
                                    <SelectTrigger data-testid="select-network">
                                        <SelectValue placeholder="Choisir un vertical" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {NETWORK_TYPES.map((l) => (
                                            <SelectItem
                                                key={l.value}
                                                value={l.value}
                                                data-testid={`network-option-${l.value}`}
                                            >
                                                {l.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                        </div>

                        <Field
                            label="Décrivez votre réseau existant *"
                            error={errors.network_description}
                            testid="field-description"
                        >
                            <Textarea
                                rows={5}
                                value={form.network_description}
                                onChange={(e) => setField("network_description", e.target.value)}
                                placeholder="Décrivez brièvement votre réseau actuel, les types de clients B2B que vous pouvez approcher et votre zone géographique principale."
                                data-testid="input-description"
                            />
                        </Field>

                        <Field label="Comment avez-vous connu CapDistrib ?" testid="field-source">
                            <Input
                                value={form.referral_source}
                                onChange={(e) => setField("referral_source", e.target.value)}
                                placeholder="LinkedIn, bouche-à-oreille, recherche…"
                                data-testid="input-source"
                            />
                        </Field>

                        <div className="flex items-start gap-3 pt-1">
                            <Checkbox
                                id="consent"
                                checked={form.consent}
                                onCheckedChange={(v) => setField("consent", !!v)}
                                data-testid="input-consent"
                                className="mt-1"
                            />
                            <label
                                htmlFor="consent"
                                className="text-[13.5px] text-cap-text/85 leading-relaxed cursor-pointer"
                            >
                                J'accepte d'être contacté par l'équipe CapDistrib
                                dans le cadre de ma candidature. Mes données
                                seront utilisées uniquement à cette fin,
                                conservées 24 mois maximum, et je peux à tout
                                moment exercer mes droits RGPD via{" "}
                                <a
                                    href="mailto:contact@capdistrib.com"
                                    className="text-cap-violet underline"
                                >
                                    contact@capdistrib.com
                                </a>{" "}
                                (voir{" "}
                                <a
                                    href="/confidentialite"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-cap-violet underline"
                                >
                                    politique de confidentialité
                                </a>
                                ). *
                            </label>
                        </div>
                        {errors.consent && (
                            <p className="text-[12px] text-red-600 -mt-3">
                                {errors.consent}
                            </p>
                        )}

                        <DialogFooter className="pt-3">
                            <button
                                type="button"
                                onClick={close}
                                className="px-5 py-2.5 rounded-full text-sm font-medium text-cap-text hover:bg-cap-light"
                                data-testid="enrollment-cancel"
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                disabled={submitting}
                                className="btn-gold px-6 py-3 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-60"
                                data-testid="enrollment-submit"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        Envoi…
                                    </>
                                ) : (
                                    <>
                                        Envoyer ma candidature
                                        <ArrowRight size={16} />
                                    </>
                                )}
                            </button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, error, children, testid }) {
    return (
        <div className="space-y-1.5" data-testid={testid}>
            <Label className="text-[13px] font-medium text-cap-text">
                {label}
            </Label>
            {children}
            {error && (
                <p
                    className="text-[12px] text-red-600"
                    data-testid={`${testid}-error`}
                >
                    {error}
                </p>
            )}
        </div>
    );
}

function SuccessView({ onClose }) {
    return (
        <div className="py-6 text-center" data-testid="enrollment-success">
            <div className="mx-auto w-16 h-16 rounded-full bg-cap-gold/15 flex items-center justify-center">
                <CheckCircle2 className="text-cap-goldDeep" size={32} />
            </div>
            <h3 className="h-display mt-5 text-2xl font-bold text-cap-ink">
                Votre candidature a bien été reçue.
            </h3>
            <p className="mt-3 text-cap-textMuted max-w-md mx-auto">
                L'équipe CapDistrib vous contactera sous 48h pour échanger sur
                votre profil et définir votre niveau.
            </p>
            <button
                onClick={onClose}
                className="mt-7 btn-navy px-6 py-3 rounded-full text-sm"
                data-testid="success-close"
            >
                Fermer
            </button>
        </div>
    );
}

function initialForm(level) {
    return {
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        country_region: "",
        level: level || "business_partner",
        network_type: "",
        network_description: "",
        referral_source: "",
        consent: false,
    };
}
